import type {
  ComplianceStatusRecord,
  InspectionRecord,
} from "@/domains/inspectionHistory";
import type { Vehicle } from "./types";

const DUE_SOON_THRESHOLD_DAYS = 30;

/** Whole days from now until the given ISO date string (negative if past). */
function daysUntil(dateString: string): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.floor((new Date(dateString).getTime() - Date.now()) / msPerDay);
}

export type VehicleComplianceFlags = {
  /** Overdue for WOF/COF or registration. */
  isOverdue: boolean;
  /** Not overdue, but WOF/COF or registration is due within DUE_SOON_THRESHOLD_DAYS days. */
  isComingDue: boolean;
};

/**
 * Vehicle-level "needs attention" classification for WOF/COF + registration.
 *
 * Shared by DashboardVehicleListCard.vue (highlight styling) and
 * TripsHomeView.vue's "needs attention" list (filtering + sorting), so a
 * vehicle is judged the same way everywhere this state is surfaced.
 * `complianceStatusRows` is every `maintenance_compliance_status` row for
 * this vehicle (inspectionHistoryStore.getComplianceStatusForVehicle) -
 * checked with `.some()` since a vehicle can have more than one.
 */
export function getVehicleComplianceFlags(
  vehicle: Vehicle,
  complianceStatusRows: ComplianceStatusRecord[],
): VehicleComplianceFlags {
  const wofCofExpired = complianceStatusRows.some(
    (status) =>
      (status.days_until_due ?? 0) < 0 ||
      status.wof_cof_status?.toUpperCase().includes("EXPIRED"),
  );

  const wofCofComingDue = complianceStatusRows.some((status) => {
    const days = status.days_until_due;
    return days != null && days >= 0 && days <= DUE_SOON_THRESHOLD_DAYS;
  });

  const registrationDaysUntil = vehicle.registrationExpiry
    ? daysUntil(vehicle.registrationExpiry)
    : null;

  const registrationExpired =
    registrationDaysUntil !== null && registrationDaysUntil < 0;

  const registrationComingDue =
    registrationDaysUntil !== null &&
    registrationDaysUntil >= 0 &&
    registrationDaysUntil <= DUE_SOON_THRESHOLD_DAYS;

  return {
    isOverdue: wofCofExpired || registrationExpired,
    isComingDue: wofCofComingDue || registrationComingDue,
  };
}

/**
 * One issue category from VehicleIssuesSummaryCard.vue's compliance
 * breakdown. Deliberately one value per stat shown there (rather than the
 * coarser overdue/comingDue/ok/noRecord bucketing `getVehicleComplianceFlags`
 * produces) so a click on any given stat can filter VehicleList.vue down to
 * exactly the vehicles it counted, with no lossy remapping.
 */
export type VehicleIssueFilter =
  | "wofCofOverdue"
  | "wofCofComingDue"
  | "wofCofFailedLastCheck"
  | "wofCofAwaitingRecheck"
  | "registrationOverdue"
  | "registrationComingDue"
  | "noComplianceRecord"
  | "hasComplianceRecord";

export const VEHICLE_ISSUE_FILTER_OPTIONS: {
  value: VehicleIssueFilter;
  label: string;
}[] = [
  { value: "wofCofOverdue", label: "WOF/COF overdue" },
  { value: "wofCofComingDue", label: "WOF/COF due within 30 days" },
  { value: "wofCofFailedLastCheck", label: "WOF/COF failed last check" },
  { value: "wofCofAwaitingRecheck", label: "WOF/COF awaiting recheck" },
  { value: "registrationOverdue", label: "Registration overdue" },
  { value: "registrationComingDue", label: "Registration due within 30 days" },
  { value: "noComplianceRecord", label: "No compliance record on file" },
  { value: "hasComplianceRecord", label: "Compliance record on file" },
];

/**
 * Tests a single vehicle against one `VehicleIssueFilter` value. Mirrors the
 * per-stat logic in VehicleIssuesSummaryCard.vue exactly, so a vehicle only
 * matches a filter if it's one of the vehicles that stat counted.
 *
 * `complianceStatusRows` and `inspectionRecords` are this vehicle's own rows
 * (inspectionHistoryStore.getComplianceStatusForVehicle /
 * getRecordsForVehicle) - not the fleet-wide arrays.
 */
export function vehicleMatchesIssueFilter(
  filter: VehicleIssueFilter,
  vehicle: Vehicle,
  complianceStatusRows: ComplianceStatusRecord[],
  inspectionRecords: InspectionRecord[],
): boolean {
  switch (filter) {
    case "wofCofOverdue":
      return complianceStatusRows.some(
        (status) =>
          (status.days_until_due ?? 0) < 0 ||
          !!status.wof_cof_status?.toUpperCase().includes("EXPIRED"),
      );
    case "wofCofComingDue":
      return complianceStatusRows.some((status) => {
        const days = status.days_until_due;
        return days != null && days >= 0 && days <= DUE_SOON_THRESHOLD_DAYS;
      });
    case "wofCofFailedLastCheck":
      return complianceStatusRows.some((status) =>
        status.last_inspection_result?.toUpperCase().includes("FAIL"),
      );
    case "wofCofAwaitingRecheck":
      return inspectionRecords.some(
        (record) =>
          record.initial_result?.toUpperCase().includes("FAIL") &&
          !record.recheck_inspection_date,
      );
    case "registrationOverdue":
      return complianceStatusRows.some((status) =>
        status.registration_status?.toUpperCase().includes("EXPIRED"),
      );
    case "registrationComingDue": {
      if (!vehicle.registrationExpiry) return false;
      const days = daysUntil(vehicle.registrationExpiry);
      return days >= 0 && days <= DUE_SOON_THRESHOLD_DAYS;
    }
    case "noComplianceRecord":
      return !vehicle.externalId || complianceStatusRows.length === 0;
    case "hasComplianceRecord":
      return !!vehicle.externalId && complianceStatusRows.length > 0;
  }
}
